import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { facilityTypesApi } from '../lib/api-client';
import type { FacilityTypeDto } from '../types/api';
import useLocale from '../contexts/LocaleContext';

interface FacilityTypeSelectorProps {
  selectedFacilityTypeId: string | null;
  onFacilityTypeChange: (facilityTypeId: string | null) => void;
  className?: string;
  disabled?: boolean;
}

const FacilityTypeSelector: React.FC<FacilityTypeSelectorProps> = ({
  selectedFacilityTypeId,
  onFacilityTypeChange,
  className = '',
  disabled = false,
}) => {
  const { dictionary, locale } = useLocale();
  const [facilityTypes, setFacilityTypes] = useState<FacilityTypeDto[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchFacilityTypes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await facilityTypesApi.getAll();
      setFacilityTypes(response);
    } catch (error) {
      console.error('Error fetching facility types:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFacilityTypes();
  }, [fetchFacilityTypes]);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    onFacilityTypeChange(value === '' ? null : value);
  };

  // Helper function to get localized facility type name (memoized)
  const getLocalizedName = useCallback(
    (facilityType: FacilityTypeDto): string => {
      switch (locale) {
        case 'fr':
          return facilityType.nameFrench || facilityType.name;
        case 'rw':
          return facilityType.nameKinyarwanda || facilityType.name;
        case 'en':
        default:
          return facilityType.name;
      }
    },
    [locale]
  );

  return (
    <div className={`flex flex-col gap-1 w-full ${className}`}>
      <label className='block text-sm font-medium text-gray-700'>
        Facility Type <span className='text-red-500 ml-1'>*</span>
      </label>
      <select
        value={selectedFacilityTypeId || ''}
        onChange={handleChange}
        disabled={loading || disabled}
        className='mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed'
      >
        <option value=''>{dictionary['facilities.select_facility_type']}</option>
        {facilityTypes.map(facilityType => (
          <option key={facilityType.id} value={facilityType.id}>
            {getLocalizedName(facilityType)}
          </option>
        ))}
      </select>
      {loading && <p className='mt-1 text-xs text-gray-500'>Loading facility types...</p>}
      {selectedFacilityTypeId && (
        <p className='mt-1 text-xs text-primary'>Selected Facility Type ID: {selectedFacilityTypeId}</p>
      )}
    </div>
  );
};

export default FacilityTypeSelector;
